import { z } from 'zod';
/**
 * DISPLAY-TOKEN (MGOS fase 6, Leitstand L3 — `CONTROL-CENTER-LEITSTAND.md` §3-L3
 * and §3-L5).
 *
 * A named, revocable, audited VIEW token for the office television. It is the
 * first NON-HUMAN principal in the estate, and §3-L5 is explicit that this is
 * the dress rehearsal for the later local-LLM identity: an own credential, a
 * narrow scope, an own audit identity, and doors that work the same for every
 * principal. So the shape of this file is the shape that step reuses — a closed
 * scope vocabulary, a server-computed expiry, a secret returned once and never
 * again.
 *
 * ── THE ONE DECISION EVERYTHING ELSE FOLLOWS: A DISPLAY TOKEN CAN ONLY LOOK ──
 *
 * TV-auth v1 (§3-L2) runs the wall on a normal staff session, and its known
 * limitation is that sessions expire and MFA-plicht applies. L3 solves that
 * WITHOUT weakening the door for people: a display token is not a session, holds
 * no role, presents no factor, and reaches EXACTLY the wallboard aggregates —
 * the same three read sources plus the stream that `screens/wall.tsx` already
 * consumes, and NOTHING else. It builds on the break-glass precedent (a narrow
 * scope, hard-registered, audit indelible), inverted: break-glass buys DEPTH
 * past a mask for a person who already holds the surface; this buys a person-less
 * BREADTH-of-nothing — only the aggregates, which by construction carry no
 * row-level data at all (`control-analytics.ts` leaf gate).
 *
 * So the scope vocabulary below has EXACTLY ONE member, and that is a closed
 * decision rather than a starting point:
 *
 *   * a display token NEVER raises a role. `PlattformGuard` runs unchanged and
 *     refuses a display principal like it refuses a customer; the token is
 *     admitted to the four wallboard routes by an EXPLICIT marker
 *     (`@DisplayLesbar`) and to nothing else, and a global default-deny guard
 *     turns every other route into a 403 for it.
 *   * a display token NEVER unlocks a write. Every route it can reach is a GET.
 *   * a display token creates NO capability that a staff wallboard did not
 *     already have. The muur shows aggregates; so does the token, and it cannot
 *     reach the row-level paths (audit, drill-down, dead-letters) that a person
 *     reaches with a purpose, because those are not `wallboard` scope.
 *
 * ── WHAT IS DELIBERATELY *NOT* A SCOPE ─────────────────────────────────────
 * Widening this list later is additive and free; narrowing it is not, for the
 * reason `AUDIT_PURPOSES` and `BREAK_GLASS_SCOPES` give — a removed value makes
 * stored rows unreadable against their own schema. `wallboard` is the honest
 * count for what a television needs TODAY. A drill-down scope is not a candidate:
 * the whole point of the muur (§3-L2) is that it hangs no customer data on the
 * office wall, and a display token that could reach a row would be that promise
 * broken. The later LLM principal gets BROADER read scopes (§3-L5) — but on the
 * same model, with purpose-plicht for row-level identical to people, and it is a
 * DIFFERENT principal, not a wider display token.
 */
/** The single scope a display token can carry: the wallboard aggregates. */
export const DISPLAY_TOKEN_SCOPE = 'wallboard';
/**
 * The opaque-secret prefix, so a leaked value is recognisable in a log or a
 * paste and so the resolver can reject a non-display bearer before it hashes
 * anything. `fvdt` = Finavest display token. The hash stored in the database is
 * over the WHOLE string, prefix included — the prefix is part of the secret, not
 * a routing hint the server trusts.
 */
export const DISPLAY_TOKEN_PREFIX = 'fvdt_';
/**
 * How long a token is usable before it must be renewed, in DAYS.
 *
 * THIRTY, and the number is the point rather than the mechanism. A television
 * runs for weeks unattended, so a session's few hours (TV-auth v1's failure) is
 * too short; forever is what a credential must never be. Thirty days is "renew
 * about once a month", short enough that a token forgotten on a decommissioned
 * screen stops working on its own, long enough that renewal is not a chore.
 *
 * A SERVER constant: `expiresAt` is computed from `NOW()`, never sent by the
 * caller, and re-checked against the clock at EVERY resolve — so a client that
 * lies about the time is asking a question the server does not read. There is no
 * "extend" endpoint on purpose: renewal is create-new + revoke-old, so every
 * token has one birth and one death in the trail, and a token cannot be quietly
 * kept alive forever the way a slid expiry could.
 *
 * Exported so the control app renders the countdown from the same number the
 * server enforces, and the contract test asserts the stored expiry is really
 * this far out rather than trusting the service.
 */
export const DISPLAY_TOKEN_TTL_TAGE = 30;
/** Name bounds. A device LABEL ("Büro-TV"), not free-form prose. */
export const DISPLAY_TOKEN_NAME_MIN = 1;
export const DISPLAY_TOKEN_NAME_MAX = 120;
/** Hard bound on what one list call returns; there is no paging on this route. */
export const DISPLAY_TOKEN_LISTE_MAX = 100;
/**
 * `POST /control/display-tokens` — mint a token.
 *
 * `.strict()`, like every write schema on this prefix: an unknown field is a 400
 * rather than a silent no-op. On a route that mints a CREDENTIAL, a typo'd field
 * name must never read as "you asked for nothing".
 *
 * The body carries only a NAME. There is no `expiresAt`, no `scope`, no `dauer`:
 * the scope is always `wallboard` (there is no other), and a duration the caller
 * picks is a duration that grows.
 */
export const createDisplayTokenSchema = z
    .object({
    /**
     * The display's label, shown in the register and the sessie-overzicht so an
     * operator can tell which screen a token belongs to. Trimmed and length-
     * bounded; it is the ONE free-text field, and — like a break-glass reason —
     * it never leaves through the event payload (only its LENGTH does), because
     * "Büro-TV" is a label but the field is still operator-typed.
     */
    name: z.string().trim().min(DISPLAY_TOKEN_NAME_MIN).max(DISPLAY_TOKEN_NAME_MAX),
})
    .strict();
/**
 * The state of a token, DERIVED and never stored.
 *
 * There is no `status` column, the same decision `BreakGlassGrant` and
 * `TenantModule` make: the state is fully determined by `expiresAt` and
 * `revokedAt`, so a stored copy would be a second answer that can disagree with
 * the first — and the only way it could disagree is by claiming a token is still
 * dead when the clock says it is alive, or alive when it was revoked.
 *
 *   aktiv       not revoked and now < expiresAt
 *   abgelaufen  not revoked and expired
 *   widerrufen  revoked; terminal, whatever the clock says
 */
export const DISPLAY_TOKEN_STATUS = ['aktiv', 'abgelaufen', 'widerrufen'];
